/**
 * api/client.ts — Isolated API layer for all modules.
 * Swap backend URLs here without touching any component.
 */
import type { CompareResult, HistoryJob } from '@/types'
import type {
  BriefSession,
  BriefSessionSummary,
  CreateSessionPayload,
  ExportResponse,
  SubmitClarificationsPayload,
  SubmitModuleConfigPayload,
  UpdateStructurePayload,
  WidgetDefinition,
} from '@/types/brief-builder'

// ── Generic request helper ────────────────────────────────────────────────────

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    headers: { 'Content-Type': 'application/json', ...init?.headers },
    ...init,
  })
  if (!res.ok) {
    let detail = `HTTP ${res.status}`
    try { const b = await res.json(); detail = b?.detail ?? b?.message ?? detail } catch {}
    throw new Error(detail)
  }
  return res.json() as Promise<T>
}

/** Multipart upload — does NOT set Content-Type so the browser adds the boundary. */
async function upload<T>(path: string, body: FormData): Promise<T> {
  const res = await fetch(path, { method: 'POST', body })
  if (!res.ok) {
    let detail = `HTTP ${res.status}`
    try { const b = await res.json(); detail = b?.detail ?? b?.message ?? detail } catch {}
    throw new Error(detail)
  }
  return res.json() as Promise<T>
}

// ── Report Generator ──────────────────────────────────────────────────────────

const REPORTS = '/api/reports'

export const reports = {
  listJobs: () =>
    request<HistoryJob[]>(`${REPORTS}/jobs`),
  createJob: (body: object) =>
    request<{ job_id: string; status: string; created_at: string }>(
      `${REPORTS}/jobs`, { method: 'POST', body: JSON.stringify(body) }
    ),
  getJob: (id: string) =>
    request<Record<string, unknown>>(`${REPORTS}/jobs/${id}`),
  cancelJob: (id: string) =>
    fetch(`${REPORTS}/jobs/${id}`, { method: 'DELETE' }),
  getTenants: () =>
    request<Array<{ name: string; label: string; has_prompt: boolean; has_vs: boolean }>>(
      `${REPORTS}/jobs/tenants`
    ),
  createTenant: (body: object) =>
    request<{ name: string; label: string; has_prompt: boolean; has_vs: boolean }>(
      `${REPORTS}/jobs/tenants`, { method: 'POST', body: JSON.stringify(body) }
    ),
  updateTenant: (name: string, body: object) =>
    request<{ name: string; label: string; has_prompt: boolean; has_vs: boolean }>(
      `${REPORTS}/jobs/tenants/${encodeURIComponent(name)}`, { method: 'PUT', body: JSON.stringify(body) }
    ),
  deleteTenant: (name: string) =>
    fetch(`${REPORTS}/jobs/tenants/${encodeURIComponent(name)}`, { method: 'DELETE' }),
  compareReport: (jobId: string, file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    return upload<CompareResult>(`${REPORTS}/compare/${jobId}`, fd)
  },
  previewUrl:   (id: string) => `${REPORTS}/jobs/${id}/files/html/preview`,
  htmlUrl:      (id: string) => `${REPORTS}/jobs/${id}/files/html`,
  markdownUrl:  (id: string) => `${REPORTS}/jobs/${id}/files/markdown`,
  pdfUrl:       (id: string) => `${REPORTS}/jobs/${id}/files/pdf`,
  excelUrl:     (id: string) => `${REPORTS}/jobs/${id}/files/excel`,
}

// ── Competitor ────────────────────────────────────────────────────────────────

const COMPETITOR = '/api/competitor'

export const competitor = {
  getPresets: () =>
    request<Array<{ name: string; label: string; description: string; url_count: number }>>(
      `${COMPETITOR}/jobs/presets`
    ),
  listJobs: () =>
    request<HistoryJob[]>(`${COMPETITOR}/jobs`),
  createJob: (body: object) =>
    request<{ job_id: string; status: string; created_at: string }>(
      `${COMPETITOR}/jobs`, { method: 'POST', body: JSON.stringify(body) }
    ),
  getJob: (id: string) =>
    request<Record<string, unknown>>(`${COMPETITOR}/jobs/${id}`),
  cancelJob: (id: string) =>
    fetch(`${COMPETITOR}/jobs/${id}`, { method: 'DELETE' }),
  markdownUrl: (id: string) => `${COMPETITOR}/jobs/${id}/files/markdown`,
  excelUrl:    (id: string) => `${COMPETITOR}/jobs/${id}/files/excel`,
}

// ── Context Builder ───────────────────────────────────────────────────────────

const CONTEXT = '/api/context'

export const context = {
  getPresets: () =>
    request<Array<{ name: string; label: string; description: string; company: string; product: string }>>(
      `${CONTEXT}/jobs/presets`
    ),
  listJobs: () =>
    request<HistoryJob[]>(`${CONTEXT}/jobs`),
  createJob: (body: object) =>
    request<{ job_id: string; status: string; created_at: string }>(
      `${CONTEXT}/jobs`, { method: 'POST', body: JSON.stringify(body) }
    ),
  getJob: (id: string) =>
    request<Record<string, unknown>>(`${CONTEXT}/jobs/${id}`),
  cancelJob: (id: string) =>
    fetch(`${CONTEXT}/jobs/${id}`, { method: 'DELETE' }),
  zipUrl: (id: string) => `${CONTEXT}/jobs/${id}/files/zip`,
}
// ── Brief Builder ─────────────────────────────────────────────────────────────

const BRIEF = '/api/brief-builder'

export const briefBuilder = {
  listSessions: () =>
    request<BriefSessionSummary[]>(`${BRIEF}/sessions`),
  createSession: (body: CreateSessionPayload) =>
    request<BriefSession>(`${BRIEF}/sessions`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  getSession: (id: string) =>
    request<BriefSession>(`${BRIEF}/sessions/${id}`),
  submitClarifications: (id: string, body: SubmitClarificationsPayload) =>
    request<BriefSession>(`${BRIEF}/sessions/${id}/clarify`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updateStructure: (id: string, body: UpdateStructurePayload) =>
    request<BriefSession>(`${BRIEF}/sessions/${id}/structure`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  validate: (id: string) =>
    request<BriefSession>(`${BRIEF}/sessions/${id}/validate`, { method: 'POST' }),
  configure: (id: string, body: SubmitModuleConfigPayload) =>
    request<BriefSession>(`${BRIEF}/sessions/${id}/configure`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  suggestQuestions: (id: string, context?: string) =>
    request<{ questions: string[]; survey_types: string[] }>(`${BRIEF}/sessions/${id}/suggest-questions`, {
      method: 'POST',
      body: JSON.stringify({ context: context ?? '' }),
    }),
  export: (id: string) =>
    request<ExportResponse>(`${BRIEF}/sessions/${id}/export`, { method: 'POST' }),
  downloadYamlUrl: (id: string) => `${BRIEF}/sessions/${id}/export/yaml`,
  deleteSession: (id: string) =>
    fetch(`${BRIEF}/sessions/${id}`, { method: 'DELETE' }),
}

// ── FAQ Chatbot ───────────────────────────────────────────────────────────────

const FAQ = '/api/faq'

export const faq = {
  chat: (body: { question: string; history: { role: string; content: string }[] }) =>
    request<{ answer: string; citations: { filename: string; file_id: string }[] }>(
      `${FAQ}/chat`, { method: 'POST', body: JSON.stringify(body) }
    ),
  health: () =>
    request<{ status: string; prompt_id: boolean; vector_store: boolean }>(`${FAQ}/health`),
}

// ── Widget Library ────────────────────────────────────────────────────────────

export const widgetLibrary = {
  list: (category?: string) =>
    request<WidgetDefinition[]>(
      category ? `${BRIEF}/widgets?category=${category}` : `${BRIEF}/widgets`
    ),
  get: (id: string) =>
    request<WidgetDefinition>(`${BRIEF}/widgets/${id}`),
  families: () =>
    request<{ families: string[] }>(`${BRIEF}/widgets/families`),
}